import { z } from "zod";
import { Operation } from "./operations.js";
import { Labels } from "./ssh-keys.js";

/*
 * TLS certificates for HTTPS services of load balancers. "Uploaded": the
 * owner's own certificate and private key (the key goes to the provider and
 * is never returned, stored or logged by us). "Managed": the provider gets
 * one from Let's Encrypt for the given domains and renews it; at Hetzner the
 * domains' DNS zones must be hosted in its DNS service.
 */

export const CertificateName = z.string().trim().min(1).max(128);

export const CertificateKind = z.enum(["uploaded", "managed"]);
export type CertificateKind = z.infer<typeof CertificateKind>;

export const CertificateIssuance = z.enum(["pending", "completed", "failed"]);
export const CertificateRenewal = z.enum(["scheduled", "pending", "failed", "unavailable"]);

/** A domain of a managed certificate; `*.example.com` covers one level of subdomains. */
export const CertificateDomain = z
  .string()
  .trim()
  .toLowerCase()
  .regex(
    /^(\*\.)?(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/,
    "Not a domain name such as example.com or *.example.com",
  );

/** A certificate as the provider adapter reports it (without our id and users). */
export const CertificateData = z.object({
  name: z.string(),
  kind: CertificateKind,
  domains: z.array(z.string()),
  /** SHA-256 fingerprint; null while a managed certificate is not issued yet. */
  fingerprint: z.string().nullable(),
  notValidBefore: z.iso.datetime().nullable(),
  notValidAfter: z.iso.datetime().nullable(),
  /** Issuance and renewal of managed certificates; null for uploaded ones. */
  status: z
    .object({
      issuance: CertificateIssuance.nullable(),
      renewal: CertificateRenewal.nullable(),
      error: z.object({ code: z.string(), message: z.string() }).nullable(),
    })
    .nullable(),
  /** Provider ids of the load balancers that use it. */
  usedByRefs: z.array(z.string()),
  labels: Labels,
  createdAt: z.iso.datetime(),
});
export type CertificateData = z.infer<typeof CertificateData>;

export const Certificate = CertificateData.extend({
  id: z.uuid(),
  /** Load balancers that present it, by our id and name (filled in by the API). */
  usedBy: z.array(z.object({ id: z.uuid().nullable(), name: z.string() })),
});
export type Certificate = z.infer<typeof Certificate>;

/** Largest PEM text we pass on: a certificate chain or a key. */
export const MAX_PEM_LENGTH = 65_536;

const Pem = (begin: RegExp, message: string) =>
  z.string().trim().min(1).max(MAX_PEM_LENGTH).regex(begin, message);

export const CreateCertificateRequest = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("uploaded"),
    name: CertificateName,
    /** The certificate, then the intermediate certificates, in PEM. */
    certificate: Pem(
      /-----BEGIN CERTIFICATE-----/,
      "Paste the certificate in PEM form (-----BEGIN CERTIFICATE-----)",
    ),
    /** The private key in PEM; sent to the provider only. */
    privateKey: Pem(
      /-----BEGIN [A-Z ]*PRIVATE KEY-----/,
      "Paste the private key in PEM form (-----BEGIN PRIVATE KEY-----)",
    ),
    labels: Labels.default({}),
  }),
  z.object({
    kind: z.literal("managed"),
    name: CertificateName,
    domains: z
      .array(CertificateDomain)
      .min(1)
      .max(100)
      .refine((d) => new Set(d).size === d.length, "Each domain once"),
    labels: Labels.default({}),
  }),
]);
export type CreateCertificateRequest = z.input<typeof CreateCertificateRequest>;

export const UpdateCertificateRequest = z
  .object({ name: CertificateName.optional(), labels: Labels.optional() })
  .refine((v) => v.name !== undefined || v.labels !== undefined, "Nothing to change");
export type UpdateCertificateRequest = z.infer<typeof UpdateCertificateRequest>;

/** Answer of writes the provider may finish later (a managed certificate's issuance). */
export const CertificateChange = z.object({ certificate: Certificate, operation: Operation });
export type CertificateChange = z.infer<typeof CertificateChange>;

/** Days before expiry from which a certificate is shown as expiring soon. */
export const EXPIRY_WARNING_DAYS = 30;

export type CertificateExpiry = "expired" | "soon" | "valid" | "unknown";

/** Where a certificate stands against `now`; managed ones not yet issued are "unknown". */
export function certificateExpiry(
  cert: Pick<CertificateData, "notValidAfter">,
  now: Date = new Date(),
): CertificateExpiry {
  if (cert.notValidAfter === null) return "unknown";
  const left = new Date(cert.notValidAfter).getTime() - now.getTime();
  if (left <= 0) return "expired";
  return left < EXPIRY_WARNING_DAYS * 86_400_000 ? "soon" : "valid";
}
